import type * as React from "react";
import {
  ArrowLeftRight,
  Box,
  ChartLine,
  LogOutIcon,
  Package,
  User,
  WalletCards,
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Link, useNavigate } from "react-router-dom";
import SidebarElement from "../sidebarElements/SidebarElements";
import SidebarUser from "../sidebarUser/SidebarUser";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useSignOut } from "@/hooks";
import { Button } from "../ui/button";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { updateUserInfo } from "@/graphql/mutations";
import ls from "@/utils/SecureLS";
import { setStylistInfo, setUser } from "@/store/slices/authSlice";
import { useState } from "react";
import { SelectSeparator } from "../ui/select";
import { Separator } from "../ui/separator";
import { listStylistInfos } from "@/graphql/queries";
import { toast } from "sonner";

const data = {
  SidebarElements: [
    {
      elementLabel: "Profile",
      items: [{ name: "Seller Profile", url: "profile", icon: User }],
    },
    {
      elementLabel: "Inventory",
      items: [
        { name: "Manage Inventory", url: "/seller", icon: Box },
        {
          name: "Add Products",
          url: "/seller/manage-inventory/add-product",
          icon: Package,
        },
      ],
    },
    {
      elementLabel: "Finance",
      items: [
        { name: "Sales Analytics", url: "/seller/sales", icon: ChartLine },
        {
          name: "Payouts",
          url: "payouts",
          icon: WalletCards,
        },
      ],
    },
  ],
};

export default function SellerSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAppSelector((state) => state.auth);
  const { handleSignOut } = useSignOut();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isStylistSwitching, setIsStylistSwitching] = useState(false);
  const [isAdminSwitching, setIsAdminSwitching] = useState(false);

  const handleSwitchRole = async () => {

    try{
      
  
    setIsStylistSwitching(true);
    const result = await executeGraphQLMutation({
      query: listStylistInfos,
      variables: {
        filter: {
          user_id: {
            eq: user?.user_id,
          },
        },
      },
    });

    if (result?.items?.length > 0 && !result?.items?.[0]?.is_verified) {
      toast.error("You are not verified as a stylist yet");
      return;
    }



    const userinfo = await executeGraphQLMutation({
      query: updateUserInfo,
      variables: {
        input: {
          id: user?.id,
          current_role: "buyer",
          updated_at: new Date().toISOString(),
        },
      },
      showToast: true,
      setLoading,
    });

    const stylistInfo = await executeGraphQLMutation({
      query: listStylistInfos,
      variables: {
        filter: {
          user_id: {
            eq: user?.user_id,
          },
        },
      },

      setLoading,
    });

    if (stylistInfo?.items?.length > 0) {
      dispatch(setStylistInfo(stylistInfo?.items[0]));
    }

    if (userinfo?.email) {
      ls.set("user", JSON.stringify(userinfo));
      dispatch(setUser(userinfo));
      navigate("/");
    }
  } catch (error) {
    console.log(error);
  } finally {
    setIsStylistSwitching(false);
  }
  };

  const handleSwitchToAdmin = async () => {
    try{
      setIsAdminSwitching(true);
    const userinfo = await executeGraphQLMutation({
      query: updateUserInfo,
      variables: {
        input: {
          id: user?.id,
          current_role: "super-admin",
          updated_at: new Date().toISOString(),
        },
      },
      showToast: true,
      setLoading,
    });

    if (userinfo?.email) {
      ls.set("user", JSON.stringify(userinfo));
      dispatch(setUser(userinfo));
      navigate("/super-admin");
    }
  } catch (error) {
    console.log(error);
  } finally {
    setIsAdminSwitching(false);
  }
  };
  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader className="">
        <SidebarMenu className="">
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:!p-1.5">
              <Link to="/seller" className="">
                <h2 className="text-lg font-semibold">Seller Dashboard</h2>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:!p-1.5 ">
              <Link to="/seller" className="">
                <div className="text-sm text-muted-foreground my-2 break-normal whitespace-normal ">
                  Manage your inventory and
                  <p className="my-0">orders</p>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarMenu>
          <SidebarMenuItem className="flex items-center gap-2 my-4 px-2 mr-1">
            <SidebarMenuButton
              tooltip="Add New Product"
              className="min-w-8 bg-primary pl-4 py-5 duration-200 ease-linear hover:bg-primary/90 hover:text-primary-foreground active:bg-primary/90 active:text-primary-foreground"
            >
              <Link
                to="/seller/manage-inventory/add-product"
                className="flex items-center gap-2 text-primary-foreground w-full"
              >
                <Package className="w-4 h-4" />
                <span className="font-medium">Add New Product</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {data?.SidebarElements.map((element) => (
          <SidebarElement
            key={element?.elementLabel}
            items={element?.items}
            elementLabel={element?.elementLabel}
          />
        ))}

        <SidebarMenu>
          <SidebarMenuItem className="mr-4">
            <SidebarMenuButton asChild onClick={handleSignOut}>
              <div className="flex hover:bg-gray-200 py-2 cursor-pointer ">
                <LogOutIcon className="text-red-600 w-4 h-4" />
                <span>Logout</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <Separator />
        <SidebarMenu>
          <SidebarMenuItem className="mr-4">
            {user?.has_roles?.includes("buyer") && (
              <Button
                disabled={isStylistSwitching}
                onClick={handleSwitchRole}
                className="group p-3 hover:bg-gray-100 hover:text-black rounded-md relative  w-44  h-11 bg-transparent text-black"
              >
                <ArrowLeftRight className="h-4 w-4 mr-2 " />
                {isStylistSwitching ? <span className="ml-4">Switching...</span> : <>Switch to Stylist</>}
              </Button>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu>
          <SidebarMenuItem className="mr-4">
            {user?.has_roles?.includes("super-admin") && (
              <Button
                disabled={isAdminSwitching}
                onClick={handleSwitchToAdmin}
                className="group p-3 hover:bg-gray-100 hover:text-black rounded-md relative  w-44  h-11 bg-transparent text-black"
              >
                <ArrowLeftRight className="h-4 w-4 mr-2 " />
                {isAdminSwitching ? <span className="ml-4">Switching...</span> : <>Switch to Admin</>}
              </Button>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarContent>
      <SidebarFooter className="pl-0">
        <SidebarUser user={user} />
      </SidebarFooter>
    </Sidebar>
  );
}
